import {makeAutoObservable} from "mobx";
import {DRAFT_ORDER_TYPE, IExchange, IRmaCustomerCase, IRmaPyment, PAYMENT_STATUS, PRODUCT_CASE_OFFER_STATUS, REFUND_LEVEL} from "../rma-types";
import ProductCaseStore from "./product-case-store";
import RmaExchangeOrderStore from "./rma-exchange-order-store";
import RmaPageStore from "./rma-page-store";
import { RESOLUTION_ID } from "../../../resolutions/constants";
import PaymentStore from "./payment/payment-store";
import customersMainStore from "../../../create-rma/stores/customers-main-store";
import CustomerStore from "../../../create-rma/stores/customer-store";
import RmaExchangeStore from "./rma-exchange-store";
import RmaPolicyRuleStore from "./rma-policy-rule-store";

class CustomerCaseStore{
	private _model?: IRmaCustomerCase;
    private _productCaseList: ProductCaseStore[] = [];
    private _exchangesList: RmaExchangeStore[] = [];
    private _exchangeOrderList: RmaExchangeOrderStore[] = [];
    private _paymentsStoreList: PaymentStore[] = [];
    private _selectedProductCase: ProductCaseStore | undefined;
	
	constructor(model:IRmaCustomerCase, private _rmaPageStore: RmaPageStore) {
		makeAutoObservable(this, {}, { autoBind: true });
        this._setModel(model);
	}

    public get id(): number|undefined { return this._model?.id;}
    public get locale(): string { return this._model?.locale ?? '';}
    public get productCaseList(): ProductCaseStore[] { return this._productCaseList; }
    public get exchangesList(): RmaExchangeStore[] { return this._exchangesList; }
    public get selectedProductCase(): ProductCaseStore | undefined { return this._selectedProductCase; }
    public get paymentCurrencyCode(): string { return this._model?.payment_currency_code ?? '';}
    public get orderName(): string { return this._model?.order_name.replace('#', '') ?? ''; }
    public get globalEOrderId(): string|null { return this._model?.glbe_order_id ?? null; }
    public get customerId(): string|undefined { return this._model?.customer_id; }
    public get exchangeOrderList(): RmaExchangeOrderStore[] { return this._exchangeOrderList; }
    public get numberOfItemsMessage(): string { return this._model?._number_of_items_message ?? ''; }
    public get reserveItemCount(): number { return this._model?._reserve_item_count ?? 0; }
    public get reserveUntil(): string|null { return this._model?._reserve_until ?? null; }
    public get exchangeOrderLIst(): RmaExchangeOrderStore[] { return this._exchangeOrderList; }
    public get feeOrder():RmaExchangeOrderStore|undefined { return this._exchangeOrderList.find(o => o.draftOrderType === DRAFT_ORDER_TYPE.DRAFT_ORDER_TYPE_FEE); }
    public get payments():IRmaPyment[] { return this._model?.payments ?? []; }
    public get returnLabelFee(): number|null { return this._model?.return_label_fee ?? null; }
    public get rmaFee(): number|null { return this._model?.rma_fee ?? null; }
    public get rmaScore(): string { return this._model?.rma_score ?? "Green"; }
    public get rmaScoreReason(): string|null { return this._model?.rma_score_reason ?? null; }
    public get cancellationFee(): number|null { return this._model?.cancellation_fee ?? null; }
    public get addressId(): string|null { return this._model?.address_id ?? null; }
    public get isGift(): boolean { return this._model?.is_gift ?? false; }
    public get usedCoverage(): boolean { return this._model?.used_coverage ?? false; }
    public get totalReturnValueAmount(): number { return this._productCaseList.reduce((acc, product) => acc + product.paidPrice, 0); }
    public get totalNewItemsValueAmount(): number { return this._exchangesList.reduce((acc, exchange) => acc + (exchange.getPriceByCurrency(this.paymentCurrencyCode) ?? 0) * exchange.quantity, 0); }
    private get totalProductCasesCount(): number { return this._model?._total_product_cases_count ?? 0; }
    public get updatedShippingAddress(): string|undefined { return this._model?._updated_shipping_address; }

    public get paymentsStoreList(): PaymentStore[] { return this._paymentsStoreList; }

    public get numberOfReferencedOutOfPolicyItems(): number {
        const acceptedProductCasesCount = this.productCaseList.length ?? 0;
        return this.totalProductCasesCount - acceptedProductCasesCount;
    }

    public isCancelResolution(): boolean {
        return this.productCaseList.some(productCase => productCase.rmaOfferStore.policyRuleStore.resolution.resolutionId === RESOLUTION_ID.CANCEL_ORDER);
    };

    public get _isTotalAmountLessThanTotalFee(): boolean {
        return (this.offerValueTotal + this.totalIncentiveAmount) < this.totalFee;
    }

    public get customer(): CustomerStore | undefined {
		return customersMainStore.getCustomerStore(this.customerId!);
	}

    public get offerValueTotal(): number {
        let total = 0;
        this.productCaseList.forEach(pc => {
            const offer = pc.rmaOfferStore;
            if (offer.status === PRODUCT_CASE_OFFER_STATUS.ACCEPTED) {
                total+= offer.credits;
            }
        });
        return total;
    }

    public get totalIncentiveAmount(): number {
        let total = 0;
        this.productCaseList.forEach(pc => {
            const offer = pc.rmaOfferStore;
            if (offer.status === PRODUCT_CASE_OFFER_STATUS.ACCEPTED) {
                total+= offer.switchToExchangeIncentiveAmount;
            }
        });
        return total;
    }

    public get totalFee(): number {
        const returnLabelFee = this.returnLabelFee ?? 0;
        const rmaFee = this.rmaFee ?? 0;
        const cancellationFee = this.cancellationFee ?? 0;

        let total = returnLabelFee + rmaFee + cancellationFee;
        this.productCaseList.forEach(pc => {
            const offer = pc.rmaOfferStore;
            if (offer.status === PRODUCT_CASE_OFFER_STATUS.ACCEPTED) {
                const offerFee = offer.fee ?? 0;
                const offerReturnLabelFee = offer.returnLabelFee ?? 0;
                total+= offerFee + offerReturnLabelFee;
            }
        });

        return total;
    }

    public get shouldConsiderCost(): boolean {
        let shouldConsiderCost = false;
        this.productCaseList.forEach(pc => {
            if (pc.rmaOfferStore.policyRuleStore.shouldConsiderCost) {
                shouldConsiderCost = true;
                return;
            }
        });
        return shouldConsiderCost;
    }

    public get hasPerShipmentFee(): boolean {
        return !!this.returnLabelFee;
    }
    public get shouldCreateFeeDraftOrder(): boolean { return this._model?._should_create_fee_draft_order ?? false; }
    public get shouldReturnLabelFeeBeAlwaysCharged(): boolean { return this._model?._should_return_label_fee_be_always_charged ?? false; }

    public get isRefunded(): boolean { 
        return !!this.productCaseList.find(productCaseStore => productCaseStore.isRefunded);
    }

    public get isCredited(): boolean { 
        return !!this.productCaseList.find(productCaseStore => productCaseStore.isCredited);
    }

    public get notValidatedProductsCount(): number {
        return this.productCaseList.filter(productCaseStore => !productCaseStore.isValidated).length;
    }
        
	private _setModel(model:IRmaCustomerCase) {
		this._model = ensureValidModel(model);
        this._productCaseList = model.accepted_product_cases.map(productCase => {
            // only if not already set - set default checkbox state for product cases in main store for persistance
            if (!this._rmaPageStore.productCasesStateMap[productCase.id])  {
                this._rmaPageStore.productCasesStateMap[productCase.id] = {
                    checked: this._rmaPageStore.defaultRefundLevel === REFUND_LEVEL.LINE_ITEM_REFUND_LEVEL
                };
            }
            return new ProductCaseStore(productCase,  this._rmaPageStore, this);
        });
        this._setExchanges(model);
        this._exchangeOrderList = model.exchange_orders.map(exchangeOrder => new RmaExchangeOrderStore(this, exchangeOrder));
        this._paymentsStoreList = model.payments
            .filter(payment => [
                PAYMENT_STATUS.AUTHORIZED,
                PAYMENT_STATUS.CHARGED,
                PAYMENT_STATUS.CANCELED,
            ].includes(payment.status))
            .map(payment => new PaymentStore(payment));
	}

    private _setExchanges(model: IRmaCustomerCase) {
        // In DB - each row in Exchange model - is an item, there could be multiple
        // rows related to same product/variant
        // Here we convert exchanges into RmaExchangeStore instances grouped by variant_id
        // and increment quantity of same RmaExchangeStore related to specific variant_id by one
        const exchanges = model.exchanges.reduce((acc: { [key: string|number]: IExchange }, exchange: IExchange) => {
            const group_id = `${exchange.variant_id}-${exchange.product_case_id}-${exchange.new_item_adjusted_price}`;
                
            if (!acc.hasOwnProperty(group_id)) {
                acc[group_id] = {...exchange, quantity: 0};  
            }
            acc[group_id].quantity++;
            return acc;
        }, {});

        // just assign array of stores to _exchangesList
        this._exchangesList = Object.values(exchanges).map(exchange => new RmaExchangeStore(exchange, this));
    }

    setSelectedProductCase(productCaseStore: ProductCaseStore){
        this._selectedProductCase = productCaseStore;
    }

    public getTotalNoShipmentRequiredItems(): number {
        return this.productCaseList.filter((productCase) => !productCase.requiresShipping && !productCase.isRejected).length;
    }

    public findPolicyRuleStoreByProductCaseId(productCaseId: number): RmaPolicyRuleStore | undefined {
        return this.productCaseList.find((productCase: ProductCaseStore) => productCase.id === productCaseId)?.rmaOfferStore.policyRuleStore;
    }

    public getProductNameByProductCaseId(productCaseId: number): string | undefined {
        const product = this.productCaseList.find((productCase: ProductCaseStore) => productCase.id === productCaseId)?.productStore;
        if (!product) {
            return undefined;
        }
        let productName = product.name;
        if (product.variantTitle) {
            productName += ` ${product.variantTitle}`;
        }
        return productName;
    }

    public getProductCasePositionOutOfTotal(): string{
        return ((this._getOpenProductCaseIndex() ?? 0) + 1) + ' / ' + this.productCaseList.length;
    }
    private _getOpenProductCaseIndex(): number | undefined{
        if(!this._selectedProductCase){
            return undefined;
        }
        return this._productCaseList.indexOf(this._selectedProductCase);
    }
    disableNextProductCaseButton(): boolean {
        return (this._getOpenProductCaseIndex() ?? 0) === this.productCaseList.length -1 ;
    }
    disablePreviousProductCaseButton(): boolean {
        return (this._getOpenProductCaseIndex() ?? 0) === 0;
    }
    nextProductCase(){
        if(this.disableNextProductCaseButton()){
            return;
        }
        this._selectedProductCase = this._productCaseList[(this._getOpenProductCaseIndex()?? 0) + 1];
    }
    previousProductCase(){
        if(this.disablePreviousProductCaseButton()){
            return;
        }
        this._selectedProductCase = this._productCaseList[(this._getOpenProductCaseIndex()?? 0) - 1];
    }
    
    unsetProductCase() {
        this._selectedProductCase = undefined;
    }
	
}

export default CustomerCaseStore;

function ensureValidModel(model: IRmaCustomerCase){
    const toNumberOrNull = (v:number|null) => { return v === null ? null : Number(v)};
    const toBool = (v: any) => Boolean(v);
    return {
        ...model,
        return_label_fee: toNumberOrNull(model.return_label_fee),
        rma_fee: toNumberOrNull(model.rma_fee),
        cancellationFee: toNumberOrNull(model.cancellation_fee),
        is_gift: toBool(model.is_gift),
        used_coverage: toBool(model.used_coverage),
    }
}
